// ФТУЕ.0: i18n-инфраструктура для приложения.
//
// Стратегия:
//   - Поддерживаем 2 языка: 'ru' (полный) и 'en' (fallback на 'ru'
//     пока переводы не залиты). По мере подготовки EN-локалей —
//     заменяем ключи в `locales/en.json`.
//   - Источник правды для текущего языка — поле player.locale на
//     сервере. При логине вызывается i18n.changeLanguage(profile.locale).
//   - До приезда сервера используем язык Telegram (Telegram.WebApp.
//     initDataUnsafe.user.language_code), а если и его нет — 'ru'.
//
// Использование в компонентах:
//   import { useTranslation } from 'react-i18next';
//   const { t } = useTranslation();
//   <h1>{t('settings.language')}</h1>
//
// Импорт этого файла должен быть ДО рендера App — см. main.tsx.

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import ru from './locales/ru.json';
import en from './locales/en.json';

export type Locale = 'ru' | 'en';
export const SUPPORTED_LOCALES: Locale[] = ['ru', 'en'];
export const DEFAULT_LOCALE: Locale = 'ru';

const LS_LOCALE_KEY = 'corps.locale';

/** Подобрать стартовый язык до того как пришёл профиль с сервера. */
function detectInitialLocale(): Locale {
  // 1) Уже выбранный пользователем язык в localStorage.
  try {
    const saved = localStorage.getItem(LS_LOCALE_KEY);
    if (saved === 'ru' || saved === 'en') return saved;
  } catch { /* ignore */ }

  // 2) Язык Telegram-аккаунта.
  //    Правило: 'ru' → RU; всё остальное (включая 'en', 'es', 'de', ...) → EN.
  //    Только русскоязычные стартуют на RU, все прочие — на EN (даже если EN
  //    пока fallback на RU из-за неполных переводов).
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tg = (window as any).Telegram?.WebApp;
    const code = String(tg?.initDataUnsafe?.user?.language_code || '').toLowerCase();
    if (code === 'ru') return 'ru';
    if (code.length > 0) return 'en';
  } catch { /* ignore */ }

  return DEFAULT_LOCALE;
}

i18next
  .use(initReactI18next)
  .init({
    resources: {
      ru: { translation: ru },
      en: { translation: en }
    },
    lng: detectInitialLocale(),
    fallbackLng: 'ru',
    interpolation: { escapeValue: false },
    returnEmptyString: false, // пустые en-ключи → используем ru (fallback)
    // Прячем шумный info-лог про initialization.
    debug: false
  });

/** Сменить язык + сохранить в localStorage. Серверный sync — отдельно. */
export function setLocaleLocal(locale: Locale) {
  i18next.changeLanguage(locale);
  try { localStorage.setItem(LS_LOCALE_KEY, locale); } catch { /* ignore */ }
}

/** Текущий язык. */
export function getLocale(): Locale {
  const l = i18next.language;
  return (l === 'en' ? 'en' : 'ru');
}

export default i18next;
